import {
  UseQueryOptions,
  UseQueryResult,
  useQuery,
  UseMutationOptions,
  UseMutationResult,
  useMutation,
}                             from 'react-query';
import { AxiosError, Method } from 'axios';
import { callApi }            from '@utils/apiCaller';
import { Notification }       from '@components/Notification/Notification';
import { showErrorMessage }   from '@utils/commonFunctions';
import {
  ISearchResult,
  ISearchParams,
}                             from '@typings/search';
import {
  IIntranetUser,
  IIntranetUserValues,
}                             from '@typings/manageIntranetUsers';
import { ROWS_PER_PAGE }      from '@utils/suggestions';

// #region FETCH INTRANET USERS LIST
const fetchIntraUsersList = async (page: number): Promise<ISearchResult<IIntranetUser>> => {
  try {
    const payload: ISearchParams = {
      offset : page * ROWS_PER_PAGE,
      limit  : ROWS_PER_PAGE,
    };

    const { data } = await callApi<ISearchResult<IIntranetUser>>(
      'intraUsers/intranet-users-search',
      'post',
      payload,
    );

    return data;
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useFetchIntraUsersList = (
  page    : number,
  config? : UseQueryOptions<ISearchResult<IIntranetUser>, AxiosError>,
): UseQueryResult<ISearchResult<IIntranetUser>, AxiosError> => useQuery(
  ['intranet-users-list', page],
  () => fetchIntraUsersList(page),
  {
    retry                : false,
    refetchOnWindowFocus : false,
    keepPreviousData     : true,
    staleTime            : 5000,
    ...config,
  },
);
// #endregion

// #region INTRANET USER DETAILS
const fetchIntraUserDetails = async (id: string): Promise<IIntranetUser> => {
  try {
    const { data } = await callApi<IIntranetUser>(`intraUsers/${id}`);

    return data;
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useFetchIntraUserDetails = (
  id      : string,
  config? : UseQueryOptions<IIntranetUser, AxiosError>,
): UseQueryResult<IIntranetUser, AxiosError> => useQuery(
  ['intranet-user-details', id],
  () => fetchIntraUserDetails(id),
  {
    retry                : false,
    refetchOnWindowFocus : false,
    ...config,
  },
);
// #endregion

// #region REMOVE INTRANET USER
const removeIntraUser = async (id: string): Promise<void> => {
  try {
    await callApi(
      `intraUsers/${id}`,
      'delete',
    );

    Notification.enqueueSnackbar(
      'User deleted successfully.',
      'info',
    );
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useRemoveIntraUser = (
  config: UseMutationOptions<void, AxiosError, string> = {},
): UseMutationResult<void, AxiosError, string> => useMutation(
  removeIntraUser,
  config,
);
// #endregion

// #region SUBMIT INTRANET USER
const submitIntraUser = async ({ id = '', confirmPassword, ...values }: IIntranetUserValues): Promise<IIntranetUser> => {
  try {
    const method  : Method = id ? 'put' : 'post';
    const message : string = id ? 'User updated successfully.' : 'User created successfully.';

    const { data } = await callApi<IIntranetUser>(
      `intraUsers/${id}`,
      method,
      values,
    );

    Notification.enqueueSnackbar(message, 'info');

    return data;
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useSubmitIntraUser = (
  config: UseMutationOptions<IIntranetUser, AxiosError, IIntranetUserValues> = {},
): UseMutationResult<IIntranetUser, AxiosError, IIntranetUserValues> => useMutation(
  submitIntraUser,
  config,
);
// #endregion
